import { Typography, useTheme } from '@mui/material'
import { keyframes, styled } from '@mui/material/styles'
import React from 'react'

import { BLUE_BASE } from '../../theme/variables/colors'

// Define a subtle loop animation
const fadeIn = keyframes`
    from {
        opacity: 0.9;
    }
    to {
        opacity: 1;
    }
`

const BlueContainer = styled('section')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: '100%',
  margin: '70px auto',
  padding: '20px',
  backgroundColor: 'rgb(221, 229, 237)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  animation: `${fadeIn} 3s ease-in-out infinite`,
  [theme.breakpoints.down('sm')]: {
    margin: '40px 20px',
  },
}))

const GridContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateRows: 'auto auto auto',
  maxWidth: '800px',
  width: '100%',
  gap: '20px',
}))

const QuoteBox = styled('blockquote')(({ theme }) => ({
  position: 'relative',
  padding: '30px',
  paddingBottom: '16px',
  margin: 0,
  fontStyle: 'italic',
  backgroundColor: '#fff',
  borderLeft: `5px solid ${BLUE_BASE}`,
  borderRight: `5px solid ${BLUE_BASE}`,
  borderRadius: '4px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    padding: '15px',
  },
}))

const TextBox = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: '0 30px',
  fontSize: '18px',
  color: 'rgb(9, 42, 72)',
  [theme.breakpoints.down('sm')]: {
    padding: '10px 15px',
  },
}))

const DecorativeSquare = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: 30,
  height: 30,
  backgroundColor: BLUE_BASE,
  borderRadius: '4px',
  [theme.breakpoints.down('sm')]: {
    width: 20,
    height: 20,
  },
}))

const LeftSquare = styled(DecorativeSquare)(({ theme }) => ({
  left: '-15px',
  top: '-15px',
}))

const RightSquare = styled(DecorativeSquare)(({ theme }) => ({
  right: '-15px',
  bottom: '-15px',
}))

const QuoteText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 300,
  fontSize: '24px',
  lineHeight: '1.5',
  color: 'rgb(9, 42, 72)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}))

const AuthorTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '1.2',
  color: 'rgb(9, 42, 72)',
  marginTop: '10px',
}))

export default function Quote({ manager_quote = {} }) {
  const theme = useTheme()
  const { quote_by, quote_text = 'hello', desc } = manager_quote

  const splitIndex = quote_by?.indexOf(',')
  const quoteAuthorName =
    splitIndex !== -1 ? quote_by.substring(0, splitIndex) : quote_by || ''
  const quoteAuthorTitle =
    splitIndex !== -1 ? quote_by.substring(splitIndex + 2) : ''

  const quoteNotEmpty = Boolean(quote_by?.trim() || quote_text?.trim())

  if (!quoteNotEmpty) {
    return null
  }

  return (
    <BlueContainer aria-labelledby="quote-section">
      <GridContainer id="quote-section">
        <QuoteBox aria-label="Manager's quote">
          <LeftSquare aria-hidden="true" />
          <QuoteText component="p">{quote_text}</QuoteText>
          <RightSquare aria-hidden="true" />
        </QuoteBox>
        <TextBox>
          <Typography variant="body2" component="cite">
            <strong>{quoteAuthorName}</strong>
          </Typography>
          {quoteAuthorTitle && (
            <AuthorTitle variant="body2">{quoteAuthorTitle}</AuthorTitle>
          )}
        </TextBox>
        {desc && (
          <TextBox>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              component="p"
              aria-label="Description"
            >
              {desc}
            </Typography>
          </TextBox>
        )}
      </GridContainer>
    </BlueContainer>
  )
}
